import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X, Upload, AlertCircle } from "lucide-react";
import { generateImageKey, isValidImageKey } from "@/lib/utils/clipboard";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface FileWithMetadata {
  file: File;
  customName: string;
  imagekey: string;
  preview: string;
  error?: string;
}

interface ImageUploadDialogProps {
  files: File[];
  onCancel: () => void;
  onUpload: (metadata: Array<{ originalName: string; customName: string; imagekey: string }>) => void;
  existingImageKeys?: string[];
}

export function ImageUploadDialog({ 
  files, 
  onCancel, 
  onUpload,
  existingImageKeys = []
}: ImageUploadDialogProps) {
  const { t } = usePlatformTranslation();
  const [filesWithMeta, setFilesWithMeta] = useState<FileWithMetadata[]>([]);

  useEffect(() => {
    // Initialize files with metadata
    const initialized = files.map((file) => {
      // Remove extension from name for initial custom name
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
      const customName = nameWithoutExt;
      const imagekey = generateImageKey(customName);

      // Create preview URL
      const preview = URL.createObjectURL(file);

      return {
        file,
        customName,
        imagekey,
        preview,
      };
    });

    setFilesWithMeta(initialized);

    // Cleanup preview URLs on unmount
    return () => {
      initialized.forEach((item) => URL.revokeObjectURL(item.preview));
    };
  }, [files]);

  const updateCustomName = (index: number, newName: string) => {
    setFilesWithMeta((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        customName: newName,
        // Auto-generate imagekey from name if user hasn't manually edited it
        imagekey: generateImageKey(newName),
      };
      return updated;
    });
  };

  const updateImageKey = (index: number, newKey: string) => {
    setFilesWithMeta((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        imagekey: newKey.toLowerCase(),
      };
      return updated;
    });
  };

  const validateAll = (): boolean => {
    const updated = filesWithMeta.map((item) => {
      let error: string | undefined;

      // Check if custom name is empty
      if (!item.customName.trim()) {
        error = "Name is required";
      }
      // Check if imagekey is empty
      else if (!item.imagekey.trim()) {
        error = "Image key is required";
      }
      // Check if imagekey is valid format
      else if (!isValidImageKey(item.imagekey)) {
        error = "Invalid format (use lowercase, numbers, and hyphens only)";
      }
      // Check for duplicate imagekeys in the current batch
      else if (filesWithMeta.filter((f) => f.imagekey === item.imagekey).length > 1) {
        error = "Duplicate image key in this upload";
      }
      // Check against existing imagekeys
      else if (existingImageKeys.includes(item.imagekey)) {
        error = "Image key already exists";
      }

      return { ...item, error };
    });

    setFilesWithMeta(updated);
    return !updated.some((item) => item.error);
  };

  const handleUpload = () => {
    if (!validateAll()) {
      return;
    }

    const metadata = filesWithMeta.map((item) => ({
      originalName: item.file.name,
      customName: item.customName,
      imagekey: item.imagekey,
    }));

    onUpload(metadata);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-semibold">{t('platform.images.imageuploaddialog.name_your_images')}</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onCancel}
            tooltip="Cancel upload"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <p className="text-sm text-gray-600 mb-4">
            Set custom names for your images. Image keys will be auto-generated but can be edited.
          </p>

          <div className="space-y-4">
            {filesWithMeta.map((item, index) => (
              <div
                key={index}
                className={`border rounded-lg p-4 ${
                  item.error ? "border-red-300 bg-red-50" : "border-gray-200"
                }`}
              >
                <div className="flex gap-4">
                  {/* Preview */}
                  <div className="flex-shrink-0">
                    <img
                      src={item.preview}
                      alt={item.file.name}
                      className="w-20 h-20 object-cover rounded border border-gray-200"
                    />
                  </div>

                  {/* Inputs */}
                  <div className="flex-1 space-y-3">
                    {/* Original Filename */}
                    <div>
                      <label className="text-xs text-gray-500 font-medium">
                        Original File
                      </label>
                      <p className="text-sm text-gray-700">{item.file.name}</p>
                    </div>

                    {/* Custom Name Input */}
                    <div>
                      <label className="text-xs text-gray-700 font-medium block mb-1">
                        Display Name *
                      </label>
                      <input
                        type="text"
                        value={item.customName}
                        onChange={(e) => updateCustomName(index, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder={t('platform.images.imageuploaddialog.eg_hero_banner')}
                      />
                    </div>

                    {/* Image Key Input */}
                    <div>
                      <label className="text-xs text-gray-700 font-medium block mb-1">
                        Image Key * <span className="text-gray-500 font-normal">(unique reference)</span>
                      </label>
                      <input
                        type="text"
                        value={item.imagekey}
                        onChange={(e) => updateImageKey(index, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm"
                        placeholder={t('platform.images.imageuploaddialog.eg_hero_banner_placeholder')}
                      />
                    </div>

                    {/* Error Message */}
                    {item.error && (
                      <div className="flex items-center gap-2 text-red-600 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        <span>{item.error}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
          <p className="text-sm text-gray-600">
            {filesWithMeta.length} image{filesWithMeta.length !== 1 ? "s" : ""} ready to upload
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={handleUpload} className="flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Upload Images
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

